import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { HeartPulse, MessageCircle, Calendar, ArrowRight } from "lucide-react";

const TITLE = "Agente de IA para clínicas e consultórios no WhatsApp | FabChat";
const DESCRIPTION =
  "Agente de IA para clínicas e consultórios: triagem no WhatsApp, respostas a dúvidas frequentes e apoio ao agendamento com CRM integrado.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Clínicas e saúde", path: "/setores/clinicas" },
];

export default function SetoresClinicas() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/setores/clinicas" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Agente de IA para clínicas e saúde</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Atendimento automatizado no WhatsApp e Instagram para clínicas: triagem inicial, dúvidas frequentes e agendamentos.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Por que usar a FabChat em clínicas</h2>
            <p className="text-muted-foreground mb-4">
              Clínicas e consultórios recebem muitas mensagens com dúvidas sobre horários, exames, preparação e agendamento. Com um <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA</Link> na FabChat, é possível fazer uma triagem inicial por WhatsApp ou Instagram: responder perguntas frequentes, informar sobre disponibilidade e agendar consultas com integração a calendário. Isso reduz o tempo de resposta e ajuda a diminuir faltas, pois o paciente recebe confirmação e lembretes no mesmo canal em que se comunicou.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Recursos que se encaixam no dia a dia</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <HeartPulse className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Triagem inicial: respostas a dúvidas frequentes (ex.: preparo para exame, documentação, convênios).</span>
              </li>
              <li className="flex gap-3">
                <Calendar className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Agendamento de consultas: verificação de disponibilidade e criação de eventos no Google Calendar.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Histórico de conversas e leads no mesmo painel (veja o <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>); encaminhamento para humano quando necessário.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Atendimento humano quando precisar</h2>
            <p className="text-muted-foreground mb-4">
              A IA não substitui o contato humano em casos sensíveis: você define em que situações o agente encaminha para um atendente ou informa que um humano retornará. A plataforma mantém o histórico da conversa para a equipe continuar o atendimento com contexto.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            Para fluxo completo, conheça o <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado da FabChat</Link> e a página de <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA para WhatsApp</Link>.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
